import { Project, ProjectCreateInput, DeleteResponse, ApiResponse } from '../types/project';

interface ApiConfig {
  baseUrl: string;
  apiKey: string;
  databaseUuid: string;
}

export class ProjectApiClient {
  private config: ApiConfig;

  constructor() {
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const apiKey = import.meta.env.VITE_API_KEY;
    const databaseUuid = import.meta.env.VITE_DATABASE_UUID;

    if (!baseUrl || !apiKey || !databaseUuid) {
      throw new Error('必要な環境変数が設定されていません。.envファイルを確認してください。');
    }

    this.config = {
      baseUrl,
      apiKey,
      databaseUuid
    };
  }

  private async fetchWithAuth<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${path}`;
    const headers = {
      'api-key': this.config.apiKey,
      'content-type': 'application/json',
      ...options.headers,
    };

    try {
      const response = await fetch(url, { ...options, headers });
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        data: {} as T,
        error: error instanceof Error ? error.message : '不明なエラーが発生しました'
      };
    }
  }

  async fetchProjects(): Promise<ApiResponse<Project[]>> {
    const params = new URLSearchParams({
      sortField: 'updatedAt',
      sortBy: 'DESC',
      skip: '0',
      limit: '20'
    });

    return this.fetchWithAuth<Project[]>(
      `/external-api/rest/${this.config.databaseUuid}/?${params.toString()}`,
      { method: 'GET' }
    );
  }

  async createProject(projectData: ProjectCreateInput): Promise<ApiResponse<Project>> {
    return this.fetchWithAuth<Project>(
      `/external-api/rest/${this.config.databaseUuid}`,
      {
        method: 'POST',
        body: JSON.stringify(this.transformProjectInput(projectData))
      }
    );
  }

  async updateProject(id: string, projectData: Partial<ProjectCreateInput>): Promise<ApiResponse<Project>> {
    return this.fetchWithAuth<Project>(
      `/external-api/rest/${this.config.databaseUuid}/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(this.transformProjectInput(projectData))
      }
    );
  }

  async deleteProject(id: string): Promise<ApiResponse<DeleteResponse>> {
    return this.fetchWithAuth<DeleteResponse>(
      `/external-api/rest/${this.config.databaseUuid}/${id}`,
      { method: 'DELETE' }
    );
  }

  private transformProjectInput(project: Partial<ProjectCreateInput>): Record<string, unknown> {
    return {
      Name: project.Name,
      deadline: project.deadline || null,
      progress_rate: project.progress_rate?.toString() || "0",
      delay_days: project.delay_days?.toString() || "0",
      status: project.status || "normal",
      memo: project.memo || null
    };
  }
}